import { BreakpointObserver } from '@angular/cdk/layout';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { computed, inject, Injectable, NgZone, PLATFORM_ID, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { NavigationStart, Params, Router } from '@angular/router';
import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  of,
  race,
  switchMap,
  take,
  timer,
} from 'rxjs';
import { LocationSearchService } from 'src/app/common/location-search/location-search.service';
import { RouteTranslateService } from 'src/app/common/route-translate/route-translate.service';
import { SlugService } from 'src/app/common/slug/slug.service';
import { UserService } from 'src/app/common/user/user.service';
import { cdn } from 'src/app/common/utils/helpers';
import { HeaderSearchService } from '../header/common/header-search/header-search.service';
import { SearchBarUserSearchCreateGQL } from './search-bar.mutation.generated';
import {
  SearchBarAutocompleteGQL,
  SearchBarAutocompleteQuery,
  SearchBarCategoriesGQL,
  SearchBarUserSearchesDocument,
  SearchBarUserSearchesGQL,
} from './search-bar.query.generated';
import {
  RecentSearch,
  SearchCategory,
  SearchLink,
  SearchOption,
  SearchOptionKind,
  SearchRegion,
} from './search-bar.models';
import { SEARCH_REGIONS } from './search-bar.regions';
import { enterZone } from '../../../common/utils/enter-zone';

type AutocompleteResult =
  | NonNullable<SearchBarAutocompleteQuery['places']>[number]
  | NonNullable<SearchBarAutocompleteQuery['results']>[number];

const MIN_TERM_LENGTH = 3;
const RECENT_SEARCHES_LIMIT = 5;
const RESULTS_LIMITS = { placesLimit: 4, contentsLimit: 3, usersLimit: 2, hashtagsLimit: 2 };
const SEARCH_DEBOUNCE_MS = 100;
const REGION_IMAGES_URL = 'https://media.sharry.land/app/images/search-bar/regioni/';
const MOBILE_QUERY = '(max-width: 1023.98px)';
const KIND_ORDER: Record<SearchOptionKind, number> = { place: 0, content: 1, hashtag: 2, all: 3 };

@Injectable({ providedIn: 'root' })
export class SearchBarStore {
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly document = inject(DOCUMENT);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly translate = inject<ITranslationService>(I18NEXT_SERVICE);
  private readonly slugService = inject(SlugService);
  private readonly userService = inject(UserService);
  private readonly routeTranslate = inject(RouteTranslateService);
  private readonly headerSearchService = inject(HeaderSearchService);
  private readonly locationSearchService = inject(LocationSearchService);
  private readonly autocompleteGQL = inject(SearchBarAutocompleteGQL);
  private readonly categoriesGQL = inject(SearchBarCategoriesGQL);
  private readonly userSearchesGQL = inject(SearchBarUserSearchesGQL);
  private readonly userSearchCreateGQL = inject(SearchBarUserSearchCreateGQL);

  readonly isMobile = toSignal(
    inject(BreakpointObserver)
      .observe(MOBILE_QUERY)
      .pipe(map(state => state.matches)),
    { initialValue: false },
  );

  readonly query = signal('');
  readonly isOpen = signal(false);
  readonly activeIndex = signal(-1);
  readonly loading = signal(false);
  readonly recentSearches = signal<RecentSearch[]>([]);
  private readonly rawCategories = signal<
    { id?: string | null; name?: string | null; normalizedName?: string | null; icon?: string | null }[]
  >([]);
  private readonly response = signal<{ term: string; results: AutocompleteResult[] }>({ term: '', results: [] });
  private panelDataRequested = false;
  private recentSearchesRequested = false;
  private readonly language = signal(this.translate.language);
  readonly hasHistoryEntry = signal(false);
  readonly keyboardOpen = signal(false);

  readonly term = computed(() => sanitize(this.query()));
  readonly hasTerm = computed(() => this.term().length >= MIN_TERM_LENGTH);
  readonly isSettled = computed(() => !this.loading() && this.response().term === this.term());

  readonly options = computed<SearchOption[]>(() => {
    this.language();
    if (!this.hasTerm()) return [];
    const { term, results } = this.response();
    if (!isRelated(term, this.term())) return [];
    return results
      .map(result => this.toOption(result))
      .filter((option): option is SearchOption => !!option)
      .sort((a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind]);
  });

  readonly navigableOptions = computed<SearchOption[]>(() => {
    this.language();
    const options = this.options();
    if (!options.length) return options;
    const term = this.term();
    return [...options, { id: 'all', kind: 'all', ...this.resultsLink({ prefix: 'title', value: term }, term) }];
  });

  readonly noResults = computed(() => this.hasTerm() && this.isSettled() && !this.options().length);
  readonly isSearching = computed(() => this.hasTerm() && !this.isSettled() && !this.options().length);
  readonly canSubmit = computed(() => this.hasTerm() && this.isSettled() && this.options().length > 0);

  readonly categories = computed<SearchCategory[]>(() => {
    this.language();
    const base = this.routeTranslate.translateRoute('category');
    return this.rawCategories()
      .filter(({ normalizedName }) => !!normalizedName)
      .map(({ id, name, normalizedName, icon }) => ({
        id,
        icon,
        label: name,
        link: `${base}/${normalizedName}`,
      }));
  });

  readonly regions = computed<SearchRegion[]>(() => {
    this.language();
    const base = this.routeTranslate.translateRoute('find/what-to-do-in');
    return SEARCH_REGIONS.map(({ key, label }) => ({
      key,
      label,
      image: `${REGION_IMAGES_URL}${slugify(label)}.webp`,
      link: `${base}/${key}`,
    }));
  });

  constructor() {
    toObservable(computed(() => (this.isOpen() && this.hasTerm() ? this.term() : '')))
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_MS),
        distinctUntilChanged(),
        filter(Boolean),
        switchMap(term => {
          this.loading.set(true);
          return this.autocompleteGQL.fetch({ text: term, ...RESULTS_LIMITS }).pipe(
            map(({ data }) => ({ term, results: [...(data?.places ?? []), ...(data?.results ?? [])] })),
            catchError(() => of({ term, results: [] as AutocompleteResult[] })),
          );
        }),
        enterZone(this.zone),
        takeUntilDestroyed(),
      )
      .subscribe(response => {
        this.response.set(response);
        this.loading.set(false);
      });

    this.headerSearchService.queryParams$.pipe(takeUntilDestroyed()).subscribe(params => {
      if (!this.isOpen()) this.query.set(params?.value ?? '');
    });

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationStart),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.close());

    this.translate.events.languageChanged.pipe(takeUntilDestroyed()).subscribe(language => {
      if (!language || language === this.language()) return;
      this.language.set(language);
      if (this.panelDataRequested) this.loadCategories('network-only');
    });

    if (this.isBrowser) {
      fromEvent(this.document.defaultView, 'popstate')
        .pipe(takeUntilDestroyed())
        .subscribe(() => {
          if (!this.hasHistoryEntry()) return;
          this.hasHistoryEntry.set(false);
          this.close();
        });
    }
  }

  open() {
    if (this.isOpen()) return;
    this.isOpen.set(true);
    this.prefetch();
    if (this.isBrowser && this.isMobile() && !this.hasHistoryEntry()) {
      this.document.defaultView.history.pushState({ searchBar: true }, '');
      this.hasHistoryEntry.set(true);
    }
  }

  dismiss() {
    if (this.hasHistoryEntry()) {
      this.document.defaultView.history.back();
    } else {
      this.close();
    }
  }

  prefetch() {
    if (this.panelDataRequested) return;
    this.panelDataRequested = true;
    this.preconnect(cdn);
    this.loadCategories();
    this.loadRecentSearches();
  }

  private loadCategories(fetchPolicy: 'cache-first' | 'network-only' = 'cache-first') {
    this.categoriesGQL.fetch({}, { fetchPolicy }).subscribe(({ data }) =>
      this.rawCategories.set(
        (data?.allCategories ?? []).map(c => ({
          id: c.id,
          name: c.name,
          normalizedName: c.normalizedName,
          icon: c.icon?.url,
        })),
      ),
    );
  }

  private loadRecentSearches() {
    if (this.recentSearchesRequested) return;
    this.recentSearchesRequested = true;
    this.userService.user$
      .pipe(
        switchMap(user =>
          user
            ? this.userSearchesGQL
                .watch({}, { fetchPolicy: 'cache-and-network' })
                .valueChanges.pipe(map(({ data }) => data?.userSearches ?? []))
            : of([]),
        ),
      )
      .subscribe(searches =>
        this.recentSearches.set(
          searches
            .map(search => ({ ...search, searchString: readableSearch(search.searchString) }))
            .filter(search => !!search.searchString)
            .slice(0, RECENT_SEARCHES_LIMIT),
        ),
      );
  }

  close() {
    this.hasHistoryEntry.set(false);
    this.isOpen.set(false);
    this.activeIndex.set(-1);
  }

  setQuery(value: string) {
    this.query.set(value);
    this.activeIndex.set(-1);
  }

  moveActive(step: 1 | -1) {
    const count = this.navigableOptions().length;
    if (!count) return;
    this.activeIndex.update(index => {
      if (index < 0) return step > 0 ? 0 : count - 1;
      return (index + step + count) % count;
    });
  }

  submit() {
    if (!this.canSubmit()) return;
    const term = this.term();
    const target = this.resultsLink({ prefix: 'title', value: term }, term);
    const replaceUrl = this.hasHistoryEntry();
    this.onNavigate(target);
    this.router.navigate([target.link], { queryParams: target.queryParams, replaceUrl });
  }

  onNavigate(target: SearchLink, save = true) {
    if (target.queryParams) {
      this.query.set(target.queryParams['value'] ?? '');
      if (!this.isCurrentSearch(target)) this.headerSearchService.setBusy(true);
      this.headerSearchService.setQueryParams(target.queryParams);
      if (save) this.saveSearch(target);
    } else {
      this.query.set('');
    }
    this.close();
  }

  openRecentSearch(event: MouseEvent, search: RecentSearch) {
    event.preventDefault();
    if (search.searchUrl === this.router.url) {
      this.dismiss();
      return;
    }
    const replaceUrl = this.hasHistoryEntry();
    this.close();
    this.headerSearchService.setBusy(true);
    this.router.navigateByUrl(search.searchUrl, { replaceUrl });
  }

  searchNearMe() {
    if (!this.hasHistoryEntry()) {
      this.close();
      this.locationSearchService.onSearch();
      return;
    }
    race(fromEvent(this.document.defaultView, 'popstate'), timer(1000))
      .pipe(take(1))
      .subscribe(() => this.locationSearchService.onSearch());
    this.dismiss();
  }

  private isCurrentSearch({ link, queryParams }: SearchLink): boolean {
    return this.router.isActive(this.router.createUrlTree([link, 'list'], { queryParams }), {
      paths: 'exact',
      queryParams: 'exact',
      matrixParams: 'ignored',
      fragment: 'ignored',
    });
  }

  private preconnect(origin: string) {
    const link = this.document.createElement('link');
    link.rel = 'preconnect';
    link.href = origin;
    this.document.head.appendChild(link);
  }

  private saveSearch({ link, label, queryParams }: SearchLink) {
    const searchString = queryParams?.['value'] || label;
    if (!this.userService.user || !searchString) return;
    const searchUrl = this.router.createUrlTree([link], { queryParams }).toString();
    this.userSearchCreateGQL
      .mutate({ input: { searchUrl, searchString } }, { refetchQueries: [SearchBarUserSearchesDocument] })
      .subscribe();
  }

  private resultsLink(queryParams: Params, label: string): SearchLink {
    return { label, link: this.routeTranslate.translateRoute('search-results'), queryParams };
  }

  private toOption(result: AutocompleteResult): SearchOption | null {
    switch (result.__typename) {
      case 'Place':
        return {
          id: result.id,
          kind: 'place',
          ...this.resultsLink(placeParams(result), result.name),
        };
      case 'Hashtag': {
        const value = '#' + result.hashtagName;
        return {
          id: result.id,
          kind: 'hashtag',
          ...this.resultsLink({ prefix: 'title', value }, value),
          label: result.hashtagName,
        };
      }
      case 'User':
        return {
          id: result.id,
          kind: 'content',
          label: result.fullname,
          avatar: {
            id: result.id,
            name: result.name,
            surname: result.surname,
            businessName: result.businessName,
            isSmallOrganisation: result.isSmallOrganisation,
            image: result.image,
          },
          link: this.slugService.slug(result),
        };
      case 'Sharry':
      case 'Wonder':
      case 'Event':
        return {
          id: result.id,
          kind: 'content',
          label: result.title,
          image: result.coverPicture?.bestVersion?.url,
          link: this.slugService.slug(result),
        };
      default:
        return null;
    }
  }
}

const sanitize = (value: string): string =>
  (value ?? '')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[#«»\\]/g, '')
    .trim();

const isRelated = (previous: string, current: string): boolean => {
  if (!previous) return false;
  const [a, b] = [previous.toLowerCase(), current.toLowerCase()];
  return a.startsWith(b) || b.startsWith(a);
};

const readableSearch = (value: string | null | undefined): string => {
  const parts = (value ?? '')
    .split(' - ')
    .map(part => part.trim())
    .filter(part => part && !/^(map|list)$/i.test(part) && !/\w+:\d+/.test(part))
    .map(part => (/^\p{Lu}?\p{Ll}+(-\p{Ll}+)+$/u.test(part) ? part.replace(/-/g, ' ') : part));
  return [...new Set(parts)].join(' · ');
};

const slugify = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z]+/g, '-')
    .replace(/^-|-$/g, '');

const placeParams = (place: Extract<AutocompleteResult, { __typename?: 'Place' }>): Params => {
  const { name, key, latitude, longitude, isRegion, isProvince, isMunicipality } = place;
  return {
    prefix: 'location',
    value: name,
    latitude,
    longitude,
    distance: !isRegion && !isProvince ? 50 : undefined,
    region: isRegion ? key : undefined,
    province: isProvince ? key : undefined,
    municipality: isMunicipality ? key : undefined,
    city: !isRegion && !isProvince && !isMunicipality ? name.replace(', Italia', '') : undefined,
  };
};
